import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useRequest } from '../../hooks/UseRequest'
import Loading from '../../components/Loading'
import LayoutSidebar from "../../components/LayoutSidebar";
import { useDispatch, useSelector } from 'react-redux'
import { selectDropdownOptions } from '../../redux/dropdownSlice'
import { setNotification } from "../../redux/notificationSlice";
import ChirpResultsContent from './chirp_results/ChirpResultsContent'
import { getBankChangeStatusFromManagerApproval } from './chirp_results/chirpResultsHelpers'
import { useChirpReportData } from './chirp_results/useChirpReportData'
import { useBankLoginResultsPermissions } from './chirp_results/useBankLoginResultsPermissions'

// Chirp's dollar fields (e.g. "$266.01") arrive as strings, not numbers.
const parseDollarString = (value) => {
  if (typeof value === 'number') return value
  if (typeof value !== 'string') return 0
  const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

const ChirpResults = () => {
  const params = useParams();
  const requests = useRequest();
  const navi = useNavigate()
  const dispatch = useDispatch();
  const dropdownOptions = useSelector(selectDropdownOptions)

  const {
    responseData,
    isLoading,
    errorMessage,
    bankChange,
    managerApproval,
    setManagerApproval,
    customer,
  } = useChirpReportData(params?.id)
  const { hasManagerApprovalReadPermission, hasManagerApprovalUpdatePermission } = useBankLoginResultsPermissions()

  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);

  // Sorting state for tables
  const [transactionAnalysisSort, setTransactionAnalysisSort] = useState({ column: null, direction: 'asc' });
  const [expenseBreakdownSort, setExpenseBreakdownSort] = useState({ column: null, direction: 'asc' });
  const [transactionHistorySort, setTransactionHistorySort] = useState({ column: null, direction: 'asc' });

  // Collapsible state for each table
  const [expandedTransactionAnalysis, setExpandedTransactionAnalysis] = useState(true);
  const [expandedExpenseBreakdown, setExpandedExpenseBreakdown] = useState(true);
  const [expandedTransactionHistory, setExpandedTransactionHistory] = useState(true);

  // Bank change form state
  const [bankChangeStatus, setBankChangeStatus] = useState('pending');
  const [bankChangeReason, setBankChangeReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customerData = {
    name: bankChange?.customer?.label || (customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : ''),
    emailAddress: bankChange?.customer?.email_address || customer?.email_address || '',
    routingNumber: bankChange?.new_bank_account?.bank_routing_number?.number || '',
    accountNumber: bankChange?.new_bank_account?.bank_account_number_last_four || ''
  }
  const oldAccountLastFour = bankChange?.old_bank_account?.bank_account_number_last_four || ''
  const oldAccountRouting = bankChange?.old_bank_account?.bank_routing_number?.number || ''

  const findDropdownOption = (searchValue, searchBy = 'code_key') => {
    const option = dropdownOptions.filter((o) => o[searchBy] === searchValue)[0];
    return option || null;
  };

  const bankChangeManagerStatus = getBankChangeStatusFromManagerApproval(managerApproval, findDropdownOption);

  const toggleTransactionAnalysis = () => setExpandedTransactionAnalysis((prev) => !prev);
  const toggleExpenseBreakdown = () => setExpandedExpenseBreakdown((prev) => !prev);
  const toggleTransactionHistory = () => setExpandedTransactionHistory((prev) => !prev);

  const handleAccountChange = (selectedOption) => {
    setSelectedAccountIndex(selectedOption.value);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const codeKey = bankChangeStatus === 'approved'
        ? 'bank_transaction_provider_call_outcome_approved'
        : 'bank_transaction_provider_call_outcome_declined';

      const outcomeDropdownOptionId = findDropdownOption(codeKey, 'code_key')?.id;
      const managerApprovalId = managerApproval?.id;

      const patchData = {
        outcome_dropdown_option_id: outcomeDropdownOptionId,
        description: bankChangeReason.trim()
      };

      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${params.id}/decision_logic_manager_decision/${managerApprovalId}`)
      const response = await requests.modify(patchData);

      if (response.data.success) {
        setManagerApproval(response.data.data.manager_approval);
        setBankChangeStatus('pending');
        setBankChangeReason('');
      } else {
        dispatch(setNotification({ type: 'error', body: response?.data?.message }))
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error }))
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionButtons = customer?.id ? (
    <button
      type="button"
      className="button has-background-grey has-text-white is-small header-button"
      onClick={() => navi(`/customer/${customer.id}`)}
    >
      <span className="mx-3">Customer Details</span>
    </button>
  ) : null;

  if (isLoading) {
    return (
      <LayoutSidebar
        contentTitle="Chirp Results"
        actionButtons={actionButtons}
        content={(<div className="p-4"><Loading /></div>)}
      />
    );
  }

  if (!responseData || (typeof responseData === 'object' && Object.keys(responseData).length === 0)) {
    return (
      <LayoutSidebar
        contentTitle="Chirp Results"
        actionButtons={actionButtons}
        content={(
          <div className="has-text-centered p-6">
            <p className="mt-3">{errorMessage || 'Response data not available'}</p>
          </div>
        )}
      />
    );
  }

  const {
    RequestCode,
    Success,
    Accounts = [],
    TransactionAnalysisSummaries = [],
    TransactionSummaries = [],
    AccountExpenses = [],
    debitCardEnrichment = []
  } = responseData;

  const selectedAccount = Accounts[selectedAccountIndex] || null;

  const accountOptions = Accounts.map((account, index) => ({
    value: index,
    label: `${account.name || account.type || 'Account'} - ${account.account_number || ''} (${account.type || ''})`,
  }));

  const normalizedAnalysis = TransactionAnalysisSummaries.map((summary) => ({
    category: summary.categoryName || summary.category,
    totalCount: summary.total,
    totalAmount: parseDollarString(summary.amount),
    recentCount: summary.recentTransactionCount,
    recentAmount: parseDollarString(summary.recentTransactionAmount),
  }));

  const normalizedExpenses = AccountExpenses.map((expense) => ({
    category: expense.category,
    amount: parseDollarString(expense.amount),
    percent: parseFloat(expense.percent) || 0,
  }));

  const selectedAccountTransactions = selectedAccount
    ? TransactionSummaries.filter((transaction) => transaction.account_id === selectedAccount.account_id)
    : [];

  const normalizedTransactions = selectedAccountTransactions.map((transaction, index) => ({
    key: transaction.guid || transaction.id || index,
    date: transaction.date || transaction.transacted_at,
    description: transaction.description,
    amount: transaction.amount,
    category: transaction.category,
    status: transaction.status,
  }));

  const canSubmitBankChangeDecision = Boolean(
    findDropdownOption(bankChangeStatus === 'approved' ? 'bank_transaction_provider_call_outcome_approved' : 'bank_transaction_provider_call_outcome_declined', 'code_key')?.id
    && managerApproval?.id
  )

  return (
    <LayoutSidebar
      contentTitle="Chirp Results"
      actionButtons={actionButtons}
      content={(
        <ChirpResultsContent
          requestCode={RequestCode}
          success={Success}
          accounts={Accounts}
          selectedAccount={selectedAccount}
          accountOptions={accountOptions}
          selectedAccountIndex={selectedAccountIndex}
          onAccountChange={handleAccountChange}
          bankChange={bankChange}
          managerApproval={managerApproval}
          bankChangeManagerStatus={bankChangeManagerStatus}
          customerData={customerData}
          oldAccountLastFour={oldAccountLastFour}
          oldAccountRouting={oldAccountRouting}
          hasManagerApprovalReadPermission={hasManagerApprovalReadPermission}
          hasManagerApprovalUpdatePermission={hasManagerApprovalUpdatePermission}
          normalizedAnalysis={normalizedAnalysis}
          transactionAnalysisSort={transactionAnalysisSort}
          setTransactionAnalysisSort={setTransactionAnalysisSort}
          expandedTransactionAnalysis={expandedTransactionAnalysis}
          toggleTransactionAnalysis={toggleTransactionAnalysis}
          normalizedTransactions={normalizedTransactions}
          transactionHistorySort={transactionHistorySort}
          setTransactionHistorySort={setTransactionHistorySort}
          expandedTransactionHistory={expandedTransactionHistory}
          toggleTransactionHistory={toggleTransactionHistory}
          normalizedExpenses={normalizedExpenses}
          expenseBreakdownSort={expenseBreakdownSort}
          setExpenseBreakdownSort={setExpenseBreakdownSort}
          expandedExpenseBreakdown={expandedExpenseBreakdown}
          toggleExpenseBreakdown={toggleExpenseBreakdown}
          debitCardEnrichment={debitCardEnrichment}
          bankChangeStatus={bankChangeStatus}
          setBankChangeStatus={setBankChangeStatus}
          bankChangeReason={bankChangeReason}
          setBankChangeReason={setBankChangeReason}
          isSubmitting={isSubmitting}
          canSubmitBankChangeDecision={canSubmitBankChangeDecision}
          onSubmitBankChangeDecision={handleSubmit}
        />
      )}
    />
  )
}

export default ChirpResults
